import { useReducer, type Dispatch } from 'react';
import type { FormDefinition } from '../format/form-definition';
import { applyEdit, type Edit } from './edits';
import { newFormDefinition } from './new-form';
import { sameJson } from './same-json';

export interface BuilderState {
  form: FormDefinition;
  saved: FormDefinition | null;
  unsaved: boolean;
  saving: boolean;
  failureShown: boolean;
  failed: boolean;
  refusal: { reason: string; n: number; open: boolean } | null;
}

type BuilderAction =
  | { type: 'edit'; edit: Edit }
  | { type: 'saveStarted' }
  | { type: 'closeRefusal' }
  | { type: 'saveSucceeded'; definition: FormDefinition }
  | { type: 'saveFailed' }
  | { type: 'closeFailure' };

export type BuilderDispatch = Dispatch<BuilderAction>;

const initialState = ({ form, saved }: { form: FormDefinition; saved: FormDefinition | null }): BuilderState => {
  return {
    form,
    saved,
    unsaved: !sameJson(form, saved ?? newFormDefinition()),
    saving: false,
    failed: false,
    failureShown: false,
    refusal: null,
  };
};

const withForm = (state: BuilderState, form: FormDefinition): BuilderState => {
  if (form === state.form) return state;
  const unsaved = !sameJson(form, state.saved ?? newFormDefinition());
  const failed = state.failed && (unsaved || state.saved === null);
  return { ...state, form, unsaved, failed };
};

export const useBuilderState = (
  form: FormDefinition,
  saved: FormDefinition | null,
): [BuilderState, BuilderDispatch] => {
  return useReducer(reduce, { form, saved }, initialState);
};

const reduce = (state: BuilderState, action: BuilderAction): BuilderState => {
  switch (action.type) {
    case 'edit': {
      const result = applyEdit(state.form, action.edit);
      if (result.kind === 'changed') return withForm(state, result.form);
      return { ...state, refusal: { reason: result.reason, n: (state.refusal?.n ?? 0) + 1, open: true } };
    }
    case 'closeRefusal':
      return state.refusal ? { ...state, refusal: { ...state.refusal, open: false } } : state;
    case 'saveStarted':
      return { ...state, saving: true, failureShown: false };
    case 'saveSucceeded': {
      const saved = action.definition;
      const unsaved = !sameJson(state.form, saved);
      return { ...state, saved, unsaved, saving: false, failed: false };
    }
    case 'saveFailed':
      return { ...state, saving: false, failed: true, failureShown: true };
    case 'closeFailure':
      return { ...state, failureShown: false };
  }
};
